import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw } from 'lucide-react';
import { GlassButton, GlassCard } from '../ui';
import { listProductions, VideoProduction } from '../../services/api';
import ProductionCard from './ProductionCard';
import ProductionWizard from './ProductionWizard';
import ProductionDetail from './ProductionDetail';
import './VideoProduction.css';

const ProductionList: React.FC = () => {
  const [productions, setProductions] = useState<VideoProduction[]>([]);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listProductions({ limit: 20 });
      setProductions(data);
    } catch {
      // non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleWizardComplete = () => {
    setShowWizard(false);
    load();
  };

  if (showWizard) {
    return <ProductionWizard onComplete={handleWizardComplete} onCancel={() => setShowWizard(false)} />;
  }

  if (selectedId !== null) {
    return <ProductionDetail productionId={selectedId} onBack={() => setSelectedId(null)} />;
  }

  return (
    <div className="vp-list-root">
      <div className="vp-list-header">
        <h3 className="vp-list-title">Video Productions</h3>
        <div className="vp-list-actions">
          <GlassButton variant="ghost" size="sm" onClick={load} title="Refresh">
            <RefreshCw size={16} />
          </GlassButton>
          <GlassButton variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setShowWizard(true)}>
            New Production
          </GlassButton>
        </div>
      </div>

      {loading && <p className="vp-empty-text">Loading productions…</p>}

      {!loading && productions.length === 0 && (
        <GlassCard className="vp-empty-card">
          <p className="vp-empty-text">No productions yet.</p>
          <p className="vp-empty-sub">Create your first video production from a script.</p>
          <GlassButton variant="primary" icon={<Plus size={16} />} onClick={() => setShowWizard(true)}>
            New Production
          </GlassButton>
        </GlassCard>
      )}

      <div className="vp-cards-grid">
        {productions.map(p => (
          <ProductionCard
            key={p.id}
            production={p}
            onDeleted={load}
            onClick={() => setSelectedId(p.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default ProductionList;
